/** Destinations for recording chunks on OPFS. */
export interface ChunkSink {
  /** Resolves once the chunk is stored; a rejection means this and later chunks are not. */
  write(chunk: Blob): Promise<void>;
  /** Finalises the file and returns its size. Keeps everything written so far, even after a failed write. */
  close(): Promise<number>;
  /** Releases the file without finalising (used when starting failed). */
  abort(): Promise<void>;
  /** True if data is on disk as soon as a write resolves (survives a crash). */
  readonly durable: boolean;
}

/** Sync access handles exist only inside workers, so support can only be discovered by trying. */
export function workersSupported(): boolean {
  return typeof Worker !== 'undefined';
}

type Reply = { id: number; ok: boolean; bytes: number; error?: { name: string; message: string } };

function fail(error: { name: string; message: string }): Error {
  const err = new Error(error.message);
  err.name = error.name;
  return err;
}

/** Writes through a worker holding a sync access handle; each acknowledged chunk is flushed. */
export async function openDurableSink(dirName: string, fileName: string): Promise<ChunkSink> {
  const worker = new Worker(new URL('./opfsWriter.worker.ts', import.meta.url), { type: 'module' });
  const pending = new Map<number, { resolve(r: Reply): void; reject(e: Error): void }>();
  let nextId = 1;

  worker.onmessage = (e: MessageEvent<Reply>) => {
    const entry = pending.get(e.data.id);
    pending.delete(e.data.id);
    entry?.resolve(e.data);
  };
  worker.onerror = (e) => {
    for (const entry of pending.values()) entry.reject(new Error(e.message || 'Recording writer crashed'));
    pending.clear();
  };

  const request = (message: Record<string, unknown>, transfer: Transferable[] = []): Promise<Reply> =>
    new Promise((resolve, reject) => {
      const id = nextId++;
      pending.set(id, { resolve, reject });
      worker.postMessage({ ...message, id }, transfer);
    });

  const opened = await request({ type: 'open', dir: dirName, name: fileName }).catch((err: Error) => {
    worker.terminate();
    throw err;
  });
  if (!opened.ok) {
    worker.terminate();
    throw fail(opened.error!);
  }

  let closedBytes: number | null = null;
  return {
    durable: true,
    async write(chunk) {
      if (closedBytes !== null) throw new Error('The recording file is already closed');
      const buffer = await chunk.arrayBuffer();
      const reply = await request({ type: 'write', buffer }, [buffer]);
      if (!reply.ok) throw fail(reply.error!);
    },
    async close() {
      if (closedBytes !== null) return closedBytes;
      const reply = await request({ type: 'close' });
      worker.terminate();
      closedBytes = reply.bytes;
      pending.clear();
      if (!reply.ok) throw fail(reply.error!);
      return closedBytes;
    },
    async abort() {
      worker.terminate();
    },
  };
}

/** Fallback for browsers without sync access handles: bytes only become visible when the stream closes. */
export async function openWritableSink(dir: FileSystemDirectoryHandle, fileName: string): Promise<ChunkSink> {
  const handle = await dir.getFileHandle(fileName, { create: true });
  const writable = await handle.createWritable();
  return {
    durable: false,
    write: (chunk) => writable.write(chunk),
    async close() {
      await writable.close();
      return (await handle.getFile()).size;
    },
    async abort() {
      await writable.abort().catch(() => undefined);
    },
  };
}
